import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonRefresher,
  IonRefresherContent,
  IonButton,
  IonIcon,
  IonToast,
  RefresherEventDetail,
  IonList,
  IonItemGroup,
  IonItemDivider,
  IonLabel,
} from '@ionic/react';
import {
  chevronBackOutline,
  chevronForwardOutline,
  timeOutline,
  locationOutline,
  chevronDownOutline,
  chevronUpOutline,
  calendarOutline,
  documentTextOutline,
} from 'ionicons/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import ProFilterChips from '../../components/pro/ProFilterChips';
import { ProEmptyState, ProLoadingList, ProSegmentTabs } from '../../components/pro/primitives';
import { PRO_CALENDAR_FILTER_DEFS, resolveProFilterDefs } from '../../components/pro/proFilterDefs';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CalendarEvent {
  calendar_id: number;
  reading_title: string;
  calendar_description?: string;
  reading_date: string;
  reading_time?: string;
  room_number?: string;
  no_time: boolean;
  upon_adjournment: boolean;
  upon_adjournment_of_committee_hearing: boolean;
  chamber: string;
  joint_meeting: boolean;
  committee_flag: boolean;
  bill_numbers?: string[];
  bills?: Array<{ bill_id: string; bill_number: string; [key: string]: unknown }>;
  tracked_bills?: Array<{ bill_id: string; bill_number: string; [key: string]: unknown }>;
}

interface EventGroup {
  date: string;
  events: CalendarEvent[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (dateStr: string, days: number): string => {
  const date = new Date(dateStr + 'T00:00:00');
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
};

const getChamberColor = (chamber: string) => {
  switch (chamber?.toLowerCase()) {
    case 'senate': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    case 'house': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    default: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
  }
};

const formatShortDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ---------------------------------------------------------------------------
// Event Card
// ---------------------------------------------------------------------------

interface EventCardProps {
  event: CalendarEvent;
  isExpanded: boolean;
  onToggle: (id: number) => void;
  trackedBillIds: Set<string>;
}

const EventCard: React.FC<EventCardProps> = ({ event, isExpanded, onToggle, trackedBillIds }) => {
  const { t } = useTranslation('pro');
  const navigate = useNavigate();

  const formatTime = (time?: string, uponAdj?: boolean): string | null => {
    if (uponAdj) return t('calendar.uponAdjournment');
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const timeLabel = formatTime(event.reading_time, event.upon_adjournment);
  const billCount = event.bills?.length ?? 0;

  const billCountLabel =
    billCount === 0
      ? t('calendar.noBills')
      : billCount === 1
      ? t('calendar.billOnAgenda')
      : t('calendar.billsOnAgenda', { count: billCount });

  return (
    <div className="bg-white dark:bg-background-dark-secondary/50 border border-border-light dark:border-border-dark rounded-lg p-3 mx-4 mb-2">
      {/* Header row: title + chamber badge */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary flex-1 min-w-0 leading-snug">
          {event.reading_title}
        </h3>
        <span
          className={`text-[11px] px-2 py-0.5 rounded flex-shrink-0 font-medium ${getChamberColor(event.chamber)}`}
        >
          {event.chamber}
        </span>
      </div>

      {/* Time + Room row */}
      {(timeLabel || event.room_number) && (
        <div className="flex items-center gap-3 text-xs text-text-tertiary dark:text-text-dark-tertiary mb-1.5">
          {timeLabel && (
            <span className="flex items-center gap-1">
              <IonIcon icon={timeOutline} className="text-xs flex-shrink-0" />
              {timeLabel}
            </span>
          )}
          {event.room_number && (
            <span className="flex items-center gap-1">
              <IonIcon icon={locationOutline} className="text-xs flex-shrink-0" />
              {event.room_number}
            </span>
          )}
        </div>
      )}

      {/* Committee badge */}
      {event.committee_flag && (
        <div className="mb-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 font-medium">
            <IonIcon icon={documentTextOutline} className="text-xs" />
            {t('calendar.committee')}
          </span>
        </div>
      )}

      {/* Bills toggle row */}
      {billCount > 0 && (
        <button
          onClick={() => onToggle(event.calendar_id)}
          className="flex items-center justify-between w-full text-xs text-primary-600 dark:text-orange-400 font-medium mt-1 pt-1 border-t border-border-light dark:border-border-dark/50"
        >
          <span>{billCountLabel}</span>
          <IonIcon icon={isExpanded ? chevronUpOutline : chevronDownOutline} className="text-sm" />
        </button>
      )}

      {/* Expanded bill list */}
      {isExpanded && billCount > 0 && (
        <div className="mt-2 pt-2 border-t border-border-light dark:border-border-dark space-y-1">
          {event.bills!.map((bill) => (
            <button
              key={bill.bill_number || bill.bill_id}
              onClick={() => navigate(`/pro/bills/${bill.bill_number}`)}
              className="flex items-center justify-between w-full py-1.5 px-2 rounded hover:bg-background-tertiary dark:hover:bg-background-dark-secondary/50 transition-colors"
            >
              <span className="text-sm font-semibold text-primary-600 dark:text-orange-400">
                {bill.bill_number}
              </span>
              {trackedBillIds.has(String(bill.bill_id)) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                  {t('calendar.trackedBill')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const ProCalendarPage: React.FC = () => {
  const { t } = useTranslation('pro');

  // ── State ────────────────────────────────────────────────────────────────

  const [startDate, setStartDate] = useState<string>(() => toLocalDateString(new Date()));
  const [myEventsOnly, setMyEventsOnly] = useState(true);
  const [chamberFilter, setChamberFilter] = useState('');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [showError, setShowError] = useState(false);

  const endDate = useMemo(() => addDays(startDate, 6), [startDate]);
  const todayStr = toLocalDateString(new Date());
  const isCurrentWeek = startDate === todayStr;

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: events, isLoading, refetch } = useQuery<CalendarEvent[]>(
    ['pro-calendar-events', { startDate, endDate, chamber: chamberFilter, myEventsOnly }],
    async () => {
      const response = await api.get('/calendar/events', {
        params: {
          start_date: startDate,
          end_date: endDate,
          chamber: chamberFilter || undefined,
          my_events: myEventsOnly ? 'true' : undefined,
          limit: 100,
        },
      });
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : result?.events || [];
    },
    {
      staleTime: 30000,
      keepPreviousData: true,
      onError: () => setShowError(true),
    },
  );

  const { data: trackedData } = useQuery(
    ['pro-tracked-bills'],
    () => api.get('/workspace-bill-tracking/user-tracked-bills'),
    { staleTime: 60000 },
  );

  const trackedBillIds = useMemo<Set<string>>(() => {
    const raw = trackedData?.data?.data || trackedData?.data || [];
    const ids = Array.isArray(raw) ? raw : Array.isArray(raw?.bill_ids) ? raw.bill_ids : [];
    return new Set(ids.map((id: unknown) => String(id)));
  }, [trackedData]);

  // ── Date grouping ────────────────────────────────────────────────────────

  const groupedEvents = useMemo<EventGroup[]>(() => {
    if (!events?.length) return [];
    const map = new Map<string, CalendarEvent[]>();
    for (const evt of events) {
      const dateKey = evt.reading_date.split('T')[0];
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(evt);
    }
    const sorted = [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
    return sorted.map(([date, evts]) => ({ date, events: evts }));
  }, [events]);

  // ── Date label ───────────────────────────────────────────────────────────

  const formatDateLabel = (dateStr: string): string => {
    const tomorrowStr = addDays(todayStr, 1);
    const shortDate = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    if (dateStr === todayStr) return `${t('calendar.today')}, ${shortDate}`;
    if (dateStr === tomorrowStr) return `${t('calendar.tomorrow')}, ${shortDate}`;
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  const handlePrevWeek = () => {
    setStartDate((prev) => addDays(prev, -7));
    setExpandedEvents(new Set());
  };

  const handleNextWeek = () => {
    setStartDate((prev) => addDays(prev, 7));
    setExpandedEvents(new Set());
  };

  const handleToday = () => {
    setStartDate(todayStr);
    setExpandedEvents(new Set());
  };

  const handleToggleEvent = (id: number) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleChamberFilterChange = (key: string, value: string) => {
    if (key === 'chamber') setChamberFilter(value);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const chamberFilterDefs = resolveProFilterDefs(PRO_CALENDAR_FILTER_DEFS, t);

  const activeFilters = { chamber: chamberFilter };

  return (
    <>
      {/* Pull-to-refresh */}
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="max-w-3xl mx-auto pb-6">
        {/* My Events / All Events toggle */}
        <div className="px-4 pt-2 pb-2 flex justify-center">
          <ProSegmentTabs
            value={myEventsOnly ? 'my' : 'all'}
            onChange={(v) => setMyEventsOnly(v === 'my')}
            options={[
              { value: 'my', label: t('calendar.myEvents') },
              { value: 'all', label: t('calendar.allEvents') },
            ]}
            className="max-w-xs w-full"
          />
        </div>

        {/* Date navigation */}
        <div className="flex flex-col items-center px-4 pb-1">
          <div className="flex items-center gap-2 w-full justify-between">
            <IonButton fill="clear" size="small" onClick={handlePrevWeek} className="flex-shrink-0">
              <IonIcon icon={chevronBackOutline} slot="icon-only" />
            </IonButton>
            <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary text-center">
              {formatShortDate(startDate)} – {formatShortDate(endDate)}
            </span>
            <IonButton fill="clear" size="small" onClick={handleNextWeek} className="flex-shrink-0">
              <IonIcon icon={chevronForwardOutline} slot="icon-only" />
            </IonButton>
          </div>
          {!isCurrentWeek && (
            <IonButton fill="clear" size="small" onClick={handleToday} className="mt-0.5">
              {t('calendar.today')}
            </IonButton>
          )}
        </div>

        {/* Chamber filter chips */}
        <ProFilterChips
          filters={chamberFilterDefs}
          activeFilters={activeFilters}
          onFilterChange={handleChamberFilterChange}
        />

        {/* Event list */}
        {isLoading ? (
          <div className="px-4 mt-2">
            <ProLoadingList variant="calendar-day" count={2} />
          </div>
        ) : groupedEvents.length === 0 && !isLoading ? (
          <ProEmptyState icon={calendarOutline} message={t('calendar.noEvents')} />
        ) : (
          <IonList lines="none" className="ion-no-padding">
            {groupedEvents.map((group) => (
              <IonItemGroup key={group.date}>
                <IonItemDivider className="bg-white dark:bg-background-dark-secondary" sticky>
                  <IonLabel className="text-xs font-semibold uppercase tracking-wide text-text-secondary dark:text-text-dark-secondary py-1">
                    {formatDateLabel(group.date)}
                  </IonLabel>
                </IonItemDivider>
                <div className="pt-2 pb-1">
                  {group.events.map((event) => (
                    <EventCard
                      key={event.calendar_id}
                      event={event}
                      isExpanded={expandedEvents.has(event.calendar_id)}
                      onToggle={handleToggleEvent}
                      trackedBillIds={trackedBillIds}
                    />
                  ))}
                </div>
              </IonItemGroup>
            ))}
          </IonList>
        )}
      </div>

      {/* Error toast */}
      <IonToast
        isOpen={showError}
        onDidDismiss={() => setShowError(false)}
        message={t('calendar.loadError')}
        duration={4000}
        buttons={[
          {
            text: t('common.retry'),
            handler: () => {
              setShowError(false);
              refetch();
            },
          },
        ]}
        color="danger"
      />
    </>
  );
};

export default ProCalendarPage;
